import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import authService from '../../services/authService'
import { toast } from 'react-hot-toast'

// Initial state
const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  loginAttempts: 0,
  accountLocked: false,
  permissions: [], // User's permissions
  allowedNavigation: [] // Navigation items user has access to
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      console.log('Login attempt:', { username })
      const response = await authService.login(username, password)
      
      console.log('Login response:', response)
      
      if (response.success) {
        toast.success('Login successful!')
        console.log('Login successful, returning data:', response.data)
        return response.data
      } else {
        console.error('Login failed:', response.message)
        return rejectWithValue(response.message || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)
      const message = error.response?.data?.message || error.message || 'Login failed'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    try {
      const { token } = getState().auth
      if (token) {
        await authService.logout()
      }
      toast.success('Logged out successfully')
    } catch (error) {
      // Even if logout fails on server, we still clear local state
      console.error('Logout error:', error)
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { refreshToken } = getState().auth
      if (!refreshToken) {
        return rejectWithValue('No refresh token available')
      }

      const response = await authService.refreshToken(refreshToken)
      
      if (response.success) {
        return response.data
      } else {
        return rejectWithValue(response.message || 'Token refresh failed')
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Token refresh failed'
      return rejectWithValue(message)
    }
  }
)

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token, user } = getState().auth
      
      // If no token, user is not authenticated
      if (!token) {
        return { isAuthenticated: false }
      }
      
      // Set the token in API client (import at top of file)
      const apiClient = (await import('../../services/apiClient')).default
      apiClient.setAuthToken(token)
      
      // Verify token with backend
      try {
        const response = await authService.getCurrentUser()
        
        if (response.success && response.data) {
          return {
            isAuthenticated: true,
            user: response.data.user || response.data,
            permissions: response.data.permissions || [],
            allowedNavigation: response.data.allowedNavigation || []
          }
        }
      } catch (error) {
        // Token is invalid, clear it
        apiClient.clearAuthToken()
        return { isAuthenticated: false }
      }
      
      // Fallback: if we have token and user from persisted state, trust it
      if (token && user) {
        return {
          isAuthenticated: true,
          user: user
        }
      }
      
      return { isAuthenticated: false }
    } catch (error) {
      console.error('Auth check error:', error)
      return rejectWithValue('Authentication check failed')
    }
  }
)

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword, confirmPassword }, { rejectWithValue }) => {
    try {
      const response = await authService.changePassword(currentPassword, newPassword, confirmPassword)
      
      if (response.success) {
        toast.success('Password changed successfully!')
        return response.data
      } else {
        return rejectWithValue(response.message || 'Password change failed')
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Password change failed'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const { profileAPI } = await import('../../services/userService')
      const response = await profileAPI.updateProfile(profileData)
      console.log("responseresponse",response)
      if (response.data.success) {
        toast.success('Profile updated successfully!')
        return response.data.data.user
      } else {
        return rejectWithValue(response.message || 'Profile update failed')
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Profile update failed'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export const uploadAvatar = createAsyncThunk(
  'auth/uploadAvatar',
  async (file, { rejectWithValue }) => {
    try {
      const { profileAPI } = await import('../../services/userService')
      const response = await profileAPI.uploadAvatar(file)
      
      if (response.data.success) {
        toast.success('Avatar uploaded successfully!')
        return response.data.url
      } else {
        return rejectWithValue(response.message || 'Avatar upload failed')
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Avatar upload failed'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearAuth: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.loading = false
      state.error = null
      state.loginAttempts = 0
      state.accountLocked = false
      state.permissions = []
      state.allowedNavigation = []
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload }
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload
    },
    setAllowedNavigation: (state, action) => {
      state.allowedNavigation = action.payload
    },
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1
      if (state.loginAttempts >= 5) {
        state.accountLocked = true
      }
    },
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0
      state.accountLocked = false
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        console.log('Login pending...')
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        console.log('Login fulfilled with payload:', action.payload)
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.isAuthenticated = true
        state.error = null
        state.loginAttempts = 0
        state.accountLocked = false
        // Store permissions if provided
        state.permissions = action.payload.permissions || []
        state.allowedNavigation = action.payload.allowedNavigation || []
        console.log('Auth state after login:', { 
          isAuthenticated: state.isAuthenticated, 
          hasToken: !!state.token,
          hasUser: !!state.user,
          permissions: state.permissions.length,
          navigation: state.allowedNavigation.length
        })
      })
      .addCase(login.rejected, (state, action) => {
        console.log('Login rejected:', action.payload)
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.loginAttempts += 1
        if (state.loginAttempts >= 5) {
          state.accountLocked = true
        }
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.loading = false
        state.error = null
        state.loginAttempts = 0
        state.accountLocked = false
        state.permissions = []
        state.allowedNavigation = []
      })
      
      // Refresh token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.error = null
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
      })
      
      // Check auth status
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = action.payload.isAuthenticated
        if (action.payload.user) {
          state.user = action.payload.user
        }
        if (action.payload.permissions) {
          state.permissions = action.payload.permissions
        }
        if (action.payload.allowedNavigation) {
          state.allowedNavigation = action.payload.allowedNavigation
        }
        if (!action.payload.isAuthenticated) {
          state.user = null
          state.token = null
          state.refreshToken = null
          state.permissions = []
          state.allowedNavigation = []
        }
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.loading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.refreshToken = null
        state.permissions = []
        state.allowedNavigation = []
      })
      
      // Change password
      .addCase(changePassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        // Update user to reflect password change
        if (state.user) {
          state.user.temp_password = false
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.user = { ...state.user, ...action.payload }
        state.error = null
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Upload avatar
      .addCase(uploadAvatar.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        state.loading = false
        if (state.user) {
          state.user.profile_pic = action.payload
        }
        state.error = null
      })
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

// Export actions
export const { 
  clearError, 
  clearAuth, 
  updateUser, 
  incrementLoginAttempts, 
  resetLoginAttempts,
  setPermissions,
  setAllowedNavigation
} = authSlice.actions

// Selectors
export const selectAuth = (state) => state.auth
export const selectUser = (state) => state.auth.user
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated
export const selectAuthLoading = (state) => state.auth.loading
export const selectAuthError = (state) => state.auth.error

export default authSlice.reducer